import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import NumericInput from "@/components/NumericInput";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { parseNumericLike } from "@/lib/numberFormatting";
import { createScheduledPayment, todayIso } from "@/lib/scheduledPayments";

export interface SchedulePaymentPrefill {
  supplier_name?: string;
  supplier_tax_id?: string;
  invoice_number?: string;
  invoice_date?: string | null;
  amount?: number;
  file?: File | null;
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  companyId: string | null;
  prefill?: SchedulePaymentPrefill;
  source?: "manual" | "received_pdf";
  onSaved?: () => void;
}

const isIsoDate = (s?: string | null) => !!s && /^\d{4}-\d{2}-\d{2}$/.test(s);

const SchedulePaymentDialog = ({ open, onOpenChange, companyId, prefill, source = "manual", onSaved }: Props) => {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [supplier, setSupplier] = useState("");
  const [taxId, setTaxId] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [amount, setAmount] = useState<number>(0);
  const [dueDate, setDueDate] = useState(todayIso());
  const [bank, setBank] = useState("");
  const [authorizedBy, setAuthorizedBy] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    if (!open) return;
    setSupplier(prefill?.supplier_name ?? "");
    setTaxId(prefill?.supplier_tax_id ?? "");
    setInvoiceNumber(prefill?.invoice_number ?? "");
    setInvoiceDate(isIsoDate(prefill?.invoice_date) ? prefill!.invoice_date! : "");
    setAmount(prefill?.amount ?? 0);
    setDueDate(todayIso());
    setBank("");
    setAuthorizedBy("");
    setNotes("");
    setFile(prefill?.file ?? null);
  }, [open, prefill]);

  const save = async () => {
    if (!companyId) {
      toast({ title: "Selecciona una empresa", variant: "destructive" });
      return;
    }
    if (!isIsoDate(dueDate)) {
      toast({ title: "Indica la fecha de pago", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      await createScheduledPayment(
        {
          company_id: companyId,
          supplier_name: supplier.trim(),
          supplier_tax_id: taxId.trim(),
          invoice_number: invoiceNumber.trim(),
          invoice_date: isIsoDate(invoiceDate) ? invoiceDate : null,
          amount: Number(amount) || 0,
          due_date: dueDate,
          bank_account: bank.trim(),
          authorized_by: authorizedBy.trim(),
          notes: notes.trim(),
          source,
        },
        file,
      );
      toast({ title: "Pago agendado", description: `Fecha de pago: ${dueDate.split("-").reverse().join("/")}` });
      onOpenChange(false);
      onSaved?.();
    } catch (e: any) {
      toast({ title: "No se pudo agendar el pago", description: e?.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Agendar pago</DialogTitle>
          <DialogDescription>La factura queda programada en la Agenda de pagos y avisará el día del pago.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2 space-y-1">
            <Label className="text-xs">Proveedor</Label>
            <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">NIF</Label>
            <Input value={taxId} onChange={(e) => setTaxId(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Nº factura</Label>
            <Input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Fecha factura</Label>
            <Input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Importe (€)</Label>
            <NumericInput value={amount} onChange={(v) => setAmount(parseNumericLike(v) ?? 0)} className="h-9" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold">Fecha de pago</Label>
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Autorizado por</Label>
            <Input value={authorizedBy} onChange={(e) => setAuthorizedBy(e.target.value)} placeholder="Quién valida la compra" />
          </div>
          <div className="sm:col-span-2 space-y-1">
            <Label className="text-xs">Cuenta / IBAN de pago</Label>
            <Input value={bank} onChange={(e) => setBank(e.target.value)} placeholder="ES00 0000 0000 0000 0000 0000" />
          </div>
          <div className="sm:col-span-2 space-y-1">
            <Label className="text-xs">Observaciones</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <div className="sm:col-span-2 space-y-1">
            <Label className="text-xs">Factura adjunta</Label>
            {prefill?.file ? (
              <p className="text-xs text-muted-foreground truncate">{prefill.file.name}</p>
            ) : (
              <Input type="file" accept="application/pdf,image/png,image/jpeg" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancelar</Button>
          <Button onClick={save} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            Agendar pago
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SchedulePaymentDialog;
